import { createContext, useContext } from 'react';
import type { Session } from './authClient.js';

/**
 * Session state (in-memory only — UI-SPEC interaction contract). The provider
 * lives in AuthGuard; this hook exposes the current session + lifecycle status
 * to the shell (TopBar tenant/user context) and any route that needs the
 * authenticated principal. Tokens themselves live in tokenStore, not here.
 */

/**
 * `unavailable` is NOT a failed sign-in. It means `/api/v1/me` could not be resolved —
 * a 403, a 500, a dropped connection — while the credentials are very possibly fine.
 * Treating that as `unauthenticated` wipes the tokens and remounts a blank form with no
 * message, which lets someone loop forever on a sign-in that already worked.
 */
export type SessionStatus = 'loading' | 'authenticated' | 'unauthenticated' | 'unavailable';

export interface SessionState {
  status: SessionStatus;
  session: Session | null;
  signOut: () => Promise<void>;
  exitImpersonation: () => Promise<void>;
  /**
   * Re-read `/me` without a loading flash. Call after a write that changes what the session
   * carries (the signed-in user's own name), so screens seeded from it do not go stale.
   */
  refreshSession?: () => Promise<void>;
}

export const SessionContext = createContext<SessionState | null>(null);

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    throw new Error('useSession must be used within an AuthGuard provider');
  }
  return ctx;
}
